/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-member-access, typescript/no-unsafe-return */
// @ts-nocheck -- removing type checking for now
import { action } from '@ember/object';
import Service, { service } from '@ember/service';

import window from 'ember-window-mock';

import { SEGMENT_READY_EVENT } from 'register-partners-js/metrics-adapters/local-segment';
import EventsQueue from 'register-partners-js/utils/events-queue';
import {
  getDidomiConsentFromCookie,
  getGaIDFromCookie,
  getHash,
} from 'register-partners-js/utils/get-tracking-misc';
import memoizeGetter from 'register-partners-js/utils/memoize-getter';

export default class Segment extends Service {
  @service cookies;
  @service deviceManager;
  @service metrics;
  @service cookiesConsentManager;

  identity = {};

  middlewaresInitialized = false;

  eventsQueue = new EventsQueue();

  constructor() {
    super(...arguments);
    this.initialize();

    window.addEventListener(SEGMENT_READY_EVENT, this.segmentReady);
  }

  willDestroy() {
    super.willDestroy(...arguments);
    window.removeEventListener(SEGMENT_READY_EVENT, this.segmentReady);
  }

  _getParams(params) {
    return {
      ...params,
      user_ga_id: getGaIDFromCookie(this.gaCookie),
      gdpr_consent_status: getDidomiConsentFromCookie(this.didomiCookie),
      id_tools_synch: getHash(),
      step_name: params?.event,
      is_webview: this.deviceManager.isWebview,
      command: 'onboarding_step',
    };
  }

  @action
  segmentReady() {
    // we need this in case when user doesn't do anything, example:
    // 1. user enters the page
    // 2. segment not loaded yet
    // 3. events go to the buffer
    // 4. cookie consent exists (accepted on qonto.com)
    // 5. segment was loaded
    // 6. users leaves the page (no actions by user) -> events lost
    //
    // in this example we will lose users that didn't go through the flow
    // we still need to send events if we are allowed to
    this.eventsQueue.flush();
  }

  initialize() {
    // we access the `metrics` service here to initialize the `Segment` adapter
    // so that we get access to `window.analytics`

    // oxlint-disable-next-line no-unused-expressions
    this.metrics;

    // register our custom middlewares
    if (window.analytics && !this.middlewaresInitialized) {
      window.analytics.addSourceMiddleware?.(this.addAdvancedMatching);
      window.analytics.addDestinationMiddleware?.('Facebook Pixel', [this.filterCookieParams]);

      this.middlewaresInitialized = true;
    }
  }

  identify(params) {
    this.sendOrSave({ callback: this._identify, args: [params] });
  }

  _identify(params) {
    this.metrics.identify(
      'LocalSegment',
      {
        ...params,
        user_ga_id: getGaIDFromCookie(this.gaCookie),
        gdpr_consent_status: getDidomiConsentFromCookie(this.didomiCookie),
      },
      { ...this.integrations }
    );

    // Facebook tracking via Segment needs this data to be explicitly added to
    // each tracking event because reasons, so we'll track it here manually
    // and attach it to the events in the `addAdvancedMatching()` method.
    this.identity = { ...this.identity, ...params };
  }

  page(params) {
    this.sendOrSave({ callback: this._page, args: [params] });
  }

  _page(params) {
    this.metrics.trackPage('LocalSegment', this._getParams(params), this.integrations);
  }

  track(eventName, params = {}) {
    this.sendOrSave({ callback: this._track, args: [eventName, params] });
  }

  _track(eventName, params = {}) {
    params = { ...params, event: eventName };
    this.metrics.trackEvent('LocalSegment', this._getParams(params), this.integrations);
  }

  /**
   * Reset
   *
   * @public
   * @method reset
   * @returns void
   *
   * WARNING. This method destroys Segment session and might affect
   * many metrics tools used by the Growth Team. Double check before using it:
   * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#reset-or-logout
   */
  reset() {
    this.sendOrSave({ callback: this._reset });
  }

  _reset() {
    let canUseDOM = Boolean(
      // oxlint-disable-next-line typescript/no-unnecessary-condition
      typeof window !== 'undefined' && window.document && window.document.createElement
    );

    if (canUseDOM && window.analytics) {
      window.analytics.reset();
    }
  }

  get isSegmentReady() {
    return Boolean(window.analytics);
  }

  get isSegmentBlocked() {
    return Boolean(window.analyticsFailedToLoad);
  }

  sendOrSave({ callback, args }) {
    if (this.isSegmentReady) {
      this.eventsQueue.flush();
      return callback.apply(this, args);
    }

    if (this.isSegmentBlocked) {
      this.eventsQueue.clear();
    } else {
      this.eventsQueue.push(() => callback.apply(this, args));
    }
  }

  @memoizeGetter get facebookCookies() {
    return { fbp: this.cookies.read('_fbp'), fbc: this.cookies.read('_fbc') };
  }

  @memoizeGetter get anonymousIdCookie() {
    return this.cookies.read('ajs_anonymous_id');
  }

  @memoizeGetter get gaCookie() {
    return this.cookies.read('_ga');
  }

  @memoizeGetter get didomiCookie() {
    return this.cookies.read('didomi_token');
  }

  @action addAdvancedMatching({ payload, next }) {
    // oxlint-disable-next-line no-shadow
    let action = payload.action();
    if (action === 'track') {
      payload.obj.context.traits = {
        email: this.identity.email,
        firstName: this.identity.first_name,
        lastName: this.identity.last_name,
        phone: this.identity.phone,
        ...payload.obj.context.traits,
      };

      payload.obj.properties = {
        ...this.facebookCookies,
        ...payload.obj.properties,
      };
    }
    next(payload);
  }

  @action filterCookieParams({ payload, next }) {
    // oxlint-disable-next-line no-shadow
    let action = payload.action();
    if (action === 'track') {
      delete payload.obj.properties.fbp;
      delete payload.obj.properties.fbc;
    }
    next(payload);
  }

  get integrations() {
    return {
      integrations: this.cookiesConsentManager.getSegmentIntegrations(),
    };
  }
}

declare module '@ember/service' {
  interface Registry {
    segment: Segment;
  }
}
